import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import {
	IconAtom2Outline18 as Atom,
	IconXmarkOutline18 as X,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { decodeHtmlEntities } from "@productlane/lib/src/html-entities"
import {
	isSearchingForSuggestions,
	visibleLinkSuggestions,
	visibleTagSuggestions,
} from "@productlane/lib/src/suggestions"
import { IconButton, IssueStatusIcon, Tooltip } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { SuggestedLink } from "./shared/picker-suggestions"
import {
	HoverCard,
	HoverCardContent,
	HoverCardPortal,
	HoverCardTrigger,
} from "@/components/hover-card"
import { Tag } from "@/components/tag"
import { toast } from "@/components/toast"
import { getProjectIcon } from "@/lib/projects"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { useThreadContext } from "../../thread-context"
import {
	IssuePreviewCard,
	ProjectPreviewCard,
} from "./issue-project-list-items"
import { useLinkThreadTarget } from "./use-link-thread-target"

// Threads from Linear start linked and an upvote names its item, so the job skips both.
const UNMATCHED_ORIGINS = new Set(["LINEAR", "UPVOTE"])
const STAGGER_MS = 50
const EXIT_MS = 150
// Long enough that moving the pointer to Link does not flash the preview.
const PREVIEW_OPEN_DELAY_MS = 300

// Enter: 200ms strong ease-out from 4px below, opacity only under reduced motion.
const ENTER_CLASSES =
	"transition-[opacity,transform] duration-200 ease-out-quint [@starting-style]:translate-y-1 [@starting-style]:opacity-0 motion-reduce:[@starting-style]:translate-y-0"

type SuggestionRows = ReturnType<typeof useSuggestionRows>
type SuggestionRow = SuggestionRows[number]

function useSuggestionRows(threadId: string, enabled: boolean) {
	const [rows] = useQuery(queries.threadSuggestions({ threadId }), {
		ttl: "10m",
		enabled,
	})
	return rows
}

function useSuggestionsEnabled(origin: string) {
	const workspace = useZeroWorkspace()
	const { role } = useRole()
	const tagsEnabled =
		role !== "VIEWER" &&
		workspace?.linkSuggestionsEnabled === true &&
		!UNMATCHED_ORIGINS.has(origin)
	const linksEnabled =
		tagsEnabled &&
		!!workspace?.linearOrganizationId &&
		!workspace.linearTokenRevoked
	return { tagsEnabled, linksEnabled }
}

/** The issues and projects suggested for a thread and not linked yet, best first. */
export function useSuggestedLinks({
	threadId,
	origin,
	linkedIssueIds,
	linkedProjectIds,
	enabled = true,
}: {
	threadId: string
	origin: string
	linkedIssueIds: ReadonlyArray<string>
	linkedProjectIds: ReadonlyArray<string>
	enabled?: boolean
}): Array<SuggestedLink> {
	const { linksEnabled } = useSuggestionsEnabled(origin)
	const rows = useSuggestionRows(threadId, enabled && linksEnabled)
	return useMemo(
		() =>
			enabled && linksEnabled
				? visibleLinkSuggestions(rows, {
						issueIds: linkedIssueIds,
						projectIds: linkedProjectIds,
					}).map((row) => ({
						kind:
							row.kind === "link_issue"
								? ("issue" as const)
								: ("project" as const),
						id: row.targetId,
					}))
				: [],
		[enabled, linksEnabled, rows, linkedIssueIds, linkedProjectIds],
	)
}

/** Ticks once a second while `active`, so the searching timer and its time limit stay current. */
function useNow(active: boolean) {
	const [now, setNow] = useState(() => Date.now())
	useEffect(() => {
		if (!active) return
		setNow(Date.now())
		const id = setInterval(() => setNow(Date.now()), 1000)
		return () => clearInterval(id)
	}, [active])
	return now
}

/**
 * What the Links group shows for suggestions: the searching state on a new
 * thread, then up to three issue or project cards and two tag chips. Tags need
 * no Linear; issues and projects do.
 */
export function useThreadLinkSuggestions() {
	const { thread } = useThreadContext()

	const linkedIssueIds = useMemo(
		() => thread.issues.filter((i) => !i.isDeleted).map((i) => i.id),
		[thread.issues],
	)
	const linkedProjectIds = useMemo(
		() => thread.projects.map((p) => p.id),
		[thread.projects],
	)

	const appliedTagIds = useMemo(
		() => thread.tags.map((t) => t.id),
		[thread.tags],
	)

	const { tagsEnabled, linksEnabled } = useSuggestionsEnabled(thread.origin)
	const rows = useSuggestionRows(thread.id, tagsEnabled)
	const suggestions = useMemo(
		() =>
			linksEnabled
				? visibleLinkSuggestions(rows, {
						issueIds: linkedIssueIds,
						projectIds: linkedProjectIds,
					})
				: [],
		[linksEnabled, rows, linkedIssueIds, linkedProjectIds],
	)
	const tagSuggestions = useMemo(
		() => (tagsEnabled ? visibleTagSuggestions(rows, appliedTagIds) : []),
		[tagsEnabled, rows, appliedTagIds],
	)

	const mightBeSearching =
		linksEnabled &&
		linkedIssueIds.length === 0 &&
		linkedProjectIds.length === 0 &&
		thread.suggestionsCheckedAt == null
	const now = useNow(mightBeSearching)
	const searching =
		mightBeSearching &&
		isSearchingForSuggestions({
			createdAt: thread.createdAt,
			checkedAt: thread.suggestionsCheckedAt,
			now,
		})

	return {
		suggestions,
		tagSuggestions,
		searching,
		elapsedSeconds: Math.max(0, Math.floor((now - thread.createdAt) / 1000)),
		isVisible: searching || suggestions.length > 0 || tagSuggestions.length > 0,
	}
}

/** Fades the row out, then hides it for good; at once under reduced motion. */
function useDismissSuggestion(id: string) {
	const zero = useZero()
	const [leaving, setLeaving] = useState(false)
	const dismiss = useCallback(() => {
		const run = () =>
			mutateOk(zero.mutate(mutators.suggestions.dismiss({ id }))).catch(() => {
				setLeaving(false)
				toast.error({ title: "Couldn't dismiss this suggestion. Try again." })
			})
		if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
			void run()
			return
		}
		setLeaving(true)
		window.setTimeout(() => void run(), EXIT_MS)
	}, [zero, id])
	return { leaving, dismiss }
}

export function ThreadLinkSuggestions({
	state,
}: {
	state: ReturnType<typeof useThreadLinkSuggestions>
}) {
	const { suggestions, searching, elapsedSeconds } = state
	// Cards that were already there when the thread opened appear at once; only
	// results that arrive while someone watches the search animate in.
	const sawSearch = useRef(searching)
	if (searching) sawSearch.current = true

	if (searching) {
		return (
			<div
				role="status"
				className="mt-1.5 flex h-5 items-center gap-1.5 px-1 text-xs text-tertiary transition-opacity duration-150 ease-out-quint [@starting-style]:opacity-0"
			>
				<Atom className="h-3.5 w-3.5 flex-none" />
				<span className="ai-loading-shimmer truncate">
					Searching Linear for similar issues
				</span>
				<span className="min-w-[3ch] flex-none tabular-nums">
					{elapsedSeconds > 0 ? `${elapsedSeconds}s` : ""}
				</span>
			</div>
		)
	}

	if (suggestions.length === 0) return null

	const animateIn = sawSearch.current
	return (
		<div data-testid="link-suggestions">
			{suggestions.map((suggestion, index) => (
				<SuggestionCard
					key={suggestion.id}
					suggestion={suggestion}
					enterDelayMs={animateIn ? (index + 1) * STAGGER_MS : null}
				/>
			))}
		</div>
	)
}

function SuggestionCard({
	suggestion,
	enterDelayMs,
}: {
	suggestion: SuggestionRow
	enterDelayMs: number | null
}) {
	const zero = useZero()
	const { linkIssue, linkProject } = useLinkThreadTarget()
	const [linking, setLinking] = useState(false)
	const { leaving, dismiss: onDismiss } = useDismissSuggestion(suggestion.id)
	const [previewOpen, setPreviewOpen] = useState(false)
	const [overActions, setOverActions] = useState(false)

	const issue = suggestion.kind === "link_issue" ? suggestion.targetIssue : null
	const project =
		suggestion.kind === "link_project" ? suggestion.targetProject : null

	const onLink = useCallback(async () => {
		if (linking || leaving) return
		setLinking(true)
		try {
			if (issue)
				await linkIssue(
					{ ...issue, type: "local" },
					{ notUpdateVisibility: true },
				)
			else if (project)
				await linkProject(
					{ ...project, type: "local" },
					{ notUpdateVisibility: true },
				)
			await mutateOk(
				zero.mutate(mutators.suggestions.accept({ id: suggestion.id })),
			)
		} catch {
			toast.error({
				title: "Couldn't link this suggestion. Try again from the Issue menu.",
			})
		} finally {
			setLinking(false)
		}
	}, [
		linking,
		leaving,
		issue,
		project,
		linkIssue,
		linkProject,
		zero,
		suggestion.id,
	])

	if (!issue && !project) return null
	const title = issue ? issue.title : project!.name
	const ProjectIcon = project ? getProjectIcon(project.icon) : null

	return (
		<HoverCard
			open={previewOpen && !overActions && !leaving}
			onOpenChange={setPreviewOpen}
			openDelay={PREVIEW_OPEN_DELAY_MS}
			closeDelay={100}
		>
			<HoverCardTrigger asChild>
				<div
					data-testid="link-suggestion"
					data-kind={suggestion.kind}
					role="button"
					tabIndex={0}
					aria-label={`Link ${issue ? issue.identifier : title}`}
					aria-busy={linking}
					onClick={() => void onLink()}
					onKeyDown={(e) => {
						if (e.target !== e.currentTarget) return
						if (e.key !== "Enter" && e.key !== " ") return
						e.preventDefault()
						void onLink()
					}}
					className={cn(
						"relative mt-1.5 select-none overflow-hidden rounded-lg border-[1px] border-dashed border-gray-300 bg-white p-2 text-sm outline-none hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-750 dark:focus-visible:ring-gray-600",
						linking && "opacity-60",
						enterDelayMs != null && !leaving && ENTER_CLASSES,
						leaving &&
							"opacity-0 transition-opacity duration-150 ease-out-quint",
					)}
					style={
						enterDelayMs != null && !leaving
							? { transitionDelay: `${enterDelayMs}ms` }
							: undefined
					}
				>
					<div className="flex items-center gap-1.5">
						{issue ? (
							<IssueStatusIcon
								className="h-3.5 w-3.5 flex-none opacity-80"
								status={issue.status}
								color={issue.statusColor ?? undefined}
							/>
						) : (
							ProjectIcon && (
								<ProjectIcon
									className="h-3.5 w-3.5 flex-none opacity-80"
									style={{ color: project?.color ?? undefined }}
								/>
							)
						)}
						<span className="flex-none tabular-nums text-secondary">
							{issue ? issue.identifier : "Project"}
						</span>
						<div
							className="-my-0.5 -mr-1 ml-auto flex flex-none items-center"
							onPointerEnter={() => setOverActions(true)}
							onPointerLeave={() => setOverActions(false)}
						>
							<Tooltip label="Dismiss suggestion">
								<IconButton
									Icon={X}
									size="sm"
									variant="ghost"
									aria-label="Dismiss suggestion"
									disabled={linking || leaving}
									onClick={(e) => {
										e.stopPropagation()
										onDismiss()
									}}
								/>
							</Tooltip>
						</div>
					</div>
					<div className="mt-1 truncate font-medium text-secondary">
						{title}
					</div>
				</div>
			</HoverCardTrigger>
			<HoverCardPortal>
				<HoverCardContent
					data-testid="link-suggestion-preview"
					className="w-[26rem]"
					sideOffset={12}
					align="start"
					side="left"
				>
					{issue ? (
						<IssuePreviewCard issue={{ ...issue, type: "local" }} />
					) : (
						project && (
							<ProjectPreviewCard project={{ ...project, type: "local" }} />
						)
					)}
				</HoverCardContent>
			</HoverCardPortal>
		</HoverCard>
	)
}

type TagSuggestionRow = ReturnType<
	typeof useThreadLinkSuggestions
>["tagSuggestions"][number]

/** Dashed chips after the thread's tags, for tags the customer named. */
export function ThreadTagSuggestions({
	state,
}: {
	state: ReturnType<typeof useThreadLinkSuggestions>
}) {
	const { tagSuggestions, searching } = state
	const sawSearch = useRef(searching)
	if (searching) sawSearch.current = true
	const [tagGroups] = useQuery(queries.tagGroups())
	const groupColors = useMemo(
		() => new Map(tagGroups.map((group) => [group.id, group.color])),
		[tagGroups],
	)

	if (tagSuggestions.length === 0) return null
	return (
		<>
			{tagSuggestions.map((suggestion, index) => (
				<TagSuggestionChip
					key={suggestion.id}
					suggestion={suggestion}
					groupColor={
						suggestion.targetTag
							? groupColors.get(suggestion.targetTag.tagGroupId)
							: undefined
					}
					enterDelayMs={sawSearch.current ? (index + 1) * STAGGER_MS : null}
				/>
			))}
		</>
	)
}

function TagSuggestionChip({
	suggestion,
	groupColor,
	enterDelayMs,
}: {
	suggestion: TagSuggestionRow
	groupColor: string | undefined
	enterDelayMs: number | null
}) {
	const zero = useZero()
	const { thread } = useThreadContext()
	const [adding, setAdding] = useState(false)
	const { leaving, dismiss } = useDismissSuggestion(suggestion.id)
	const tag = suggestion.targetTag

	const onAdd = useCallback(async () => {
		if (!tag || adding || leaving) return
		setAdding(true)
		try {
			// The same toggle the tag picker calls; the tag is never on the thread here.
			await mutateOk(
				zero.mutate(
					mutators.tags.linkTag({ threadId: thread.id, tagId: tag.id }),
				),
			)
			await mutateOk(
				zero.mutate(mutators.suggestions.accept({ id: suggestion.id })),
			)
		} catch {
			toast.error({
				title: "Couldn't add this tag. Try again from the Tag menu.",
			})
		} finally {
			setAdding(false)
		}
	}, [tag, adding, leaving, zero, thread.id, suggestion.id])

	if (!tag) return null
	const entering = enterDelayMs != null && !leaving
	return (
		<Tag
			variant="suggested"
			tag={tag}
			groupColor={groupColor}
			data-testid="tag-suggestion"
			role="button"
			tabIndex={0}
			aria-label={`Add tag ${tag.name}`}
			aria-busy={adding}
			tooltip={
				<span className="block max-w-64">
					{decodeHtmlEntities(suggestion.reason).replace(/\s+/g, " ").trim()}
				</span>
			}
			closeLabel="Dismiss suggestion"
			onClick={() => void onAdd()}
			onKeyDown={(e) => {
				if (e.target !== e.currentTarget) return
				if (e.key !== "Enter" && e.key !== " ") return
				e.preventDefault()
				void onAdd()
			}}
			onClose={dismiss}
			className={cn(
				adding && "opacity-60",
				entering && ENTER_CLASSES,
				leaving && "opacity-0 transition-opacity duration-150 ease-out-quint",
			)}
			style={entering ? { transitionDelay: `${enterDelayMs}ms` } : undefined}
		/>
	)
}
